import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { GoogleLogo, ArrowRight, Leaf, EnvelopeSimple, LockKey } from "@phosphor-icons/react";
import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Qualcosa è andato storto. Riprova.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function Login() {
  const { user, loading, setUser } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!loading && user) navigate(user.role === "admin" ? "/admin" : "/");
  }, [user, loading, navigate]);

  const startGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submitPassword = async (e) => {
    e.preventDefault();
    setErr("");
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/login", { email: email.trim(), password });
      // Persist the session token in localStorage (mirroring AuthCallback):
      // it survives a page refresh and is sent by the api client as a
      // Bearer fallback if the httpOnly cookie is dropped (e.g. Chrome
      // third-party cookie blocking).
      if (data?.session_token) localStorage.setItem("session_token", data.session_token);
      if (setUser) setUser(data.user);
      toast.success("Accesso riuscito");
      navigate(data.user?.role === "admin" ? "/admin" : "/");
    } catch (e) {
      const msg = formatApiErrorDetail(e?.response?.data?.detail) || "Accesso fallito";
      setErr(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-5">
      <div className="lg:col-span-3 relative overflow-hidden" style={{ background: "var(--bg-secondary)" }}>
        <img
          src="https://images.pexels.com/photos/6588382/pexels-photo-6588382.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200"
          alt="Grains flatlay"
          className="absolute inset-0 w-full h-full object-cover opacity-90"
        />
        <div className="absolute inset-0" style={{ background: "linear-gradient(120deg, rgba(26,26,24,0.55), rgba(26,26,24,0.15))" }} />
        <div className="relative h-full flex flex-col justify-between p-10 lg:p-16 text-[#FCFAF8]">
          <div className="flex items-center gap-3">
            <img src="/logo-mf.jpg" alt="Metodo Funzionale" className="w-10 h-10 rounded-full object-cover ring-1 ring-white/40" />
            <div className="label-tiny" style={{ color: "#FCFAF8" }}>Metodo Funzionale</div>
          </div>
          <div className="max-w-lg">
            <div className="label-tiny mb-4" style={{ color: "#EAE5DE" }}>Piani nutrizionali personalizzati</div>
            <h1 className="font-serif text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              Ogni pasto,<br/>
              <em className="text-[#F3EFE9]">un'informazione.</em>
            </h1>
            <p className="mt-6 max-w-md text-[15px] text-[#EAE5DE]">
              Uno spazio per il tuo percorso: piani su misura, rilievi corporei
              nel tempo e progressi tracciati con cura.
            </p>
          </div>
        </div>
      </div>

      <div className="lg:col-span-2 flex items-center justify-center p-8 lg:p-14">
        <div className="w-full max-w-sm fade-up">
          <div className="label-tiny">Accedi</div>
          <h2 className="font-serif text-4xl mt-2 tracking-tight">Bentornato.</h2>
          <p className="mt-3 text-sm text-[color:var(--text-secondary)]">
            Accesso riservato ad amministratore e clienti.
          </p>

          <form onSubmit={submitPassword} className="mt-8 space-y-4" data-testid="login-form">
            <div>
              <Label className="label-tiny">Email</Label>
              <div className="relative">
                <EnvelopeSimple size={16} weight="duotone" className="absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--text-secondary)]" />
                <Input
                  data-testid="login-email"
                  type="email"
                  required
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="tuo@indirizzo.it"
                  className="pl-9"
                />
              </div>
            </div>
            <div>
              <Label className="label-tiny">Password</Label>
              <div className="relative">
                <LockKey size={16} weight="duotone" className="absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--text-secondary)]" />
                <Input
                  data-testid="login-password"
                  type="password"
                  required
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="pl-9"
                />
              </div>
            </div>

            {err && (
              <div data-testid="login-error" className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                {err}
              </div>
            )}

            <Button
              type="submit"
              data-testid="login-submit"
              disabled={submitting}
              className="w-full h-11 rounded-full bg-[color:var(--accent)] text-white hover:bg-[color:var(--accent-hover)] hover:text-white"
            >
              {submitting ? "Accesso in corso…" : "Accedi"}
              <ArrowRight size={16} weight="bold" className="ml-2" />
            </Button>
          </form>

          <div className="mt-6 flex items-center gap-3 text-[10px] uppercase tracking-widest text-[color:var(--text-secondary)]">
            <div className="flex-1 h-px bg-[color:var(--border)]" />
            oppure
            <div className="flex-1 h-px bg-[color:var(--border)]" />
          </div>

          <Button
            data-testid="google-login-btn"
            variant="outline"
            onClick={startGoogleLogin}
            className="mt-6 w-full h-11 rounded-full"
          >
            <GoogleLogo size={16} weight="bold" className="mr-2" />
            Continua con Google
          </Button>

          <p className="text-xs text-[color:var(--text-secondary)] mt-6 leading-relaxed">
            Non hai una password? Chiedila all'amministratore. Se hai un account Google
            collegato puoi usarlo direttamente qui sopra.
          </p>
        </div>
      </div>
    </div>
  );
}
